import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, UserCheck, Briefcase, TrendingUp, Clock, Star,
  AlertTriangle, CheckCircle, CreditCard, UserPlus, UserMinus,
  BarChart3, DollarSign, RefreshCw, X
} from "lucide-react";
import { 
  mockPros, mockCustomers, mockJobs, mockPayments, 
  mockActivityFeed, mockChartData 
} from "./adminMockData";

// Inject font loader
if (!document.getElementById("__jmf")) {
  const l = document.createElement("link");
  l.id = "__jmf";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap";
  document.head.appendChild(l);
}

const CHART_DATA = {
  thisweek: {
    jobs: [
      { month: "Mon", jobs: 8 }, { month: "Tue", jobs: 12 }, { month: "Wed", jobs: 7 },
      { month: "Thu", jobs: 15 }, { month: "Fri", jobs: 19 }, { month: "Sat", jobs: 11 },
      { month: "Sun", jobs: 6 },
    ],
    revenue: [
      { month: "Mon", revenue: 16000 }, { month: "Tue", revenue: 24000 }, { month: "Wed", revenue: 14000 },
      { month: "Thu", revenue: 30000 }, { month: "Fri", revenue: 38000 }, { month: "Sat", revenue: 22000 },
      { month: "Sun", revenue: 12000 },
    ],
    jobsTotal: 78,
    revenueTotal: 156000,
    jobsTrend: "22%",
    revenueTrend: "18%",
    jobsSubtitle: "Last 7 days",
    revenueSubtitle: "Last 7 days",
  },
  thismonth: {
    jobs: [
      { month: "W1", jobs: 54 }, { month: "W2", jobs: 67 }, { month: "W3", jobs: 48 }, { month: "W4", jobs: 71 },
    ],
    revenue: [
      { month: "W1", revenue: 108000 }, { month: "W2", revenue: 134000 },
      { month: "W3", revenue: 96000 }, { month: "W4", revenue: 142000 },
    ],
    jobsTotal: 240,
    revenueTotal: 480000,
    jobsTrend: "14%",
    revenueTrend: "11%",
    jobsSubtitle: "Last 30 days",
    revenueSubtitle: "Last 30 days",
  },
  last6months: {
    jobs: [
      { month: "Feb", jobs: 143 }, { month: "Mar", jobs: 189 }, { month: "Apr", jobs: 221 },
      { month: "May", jobs: 198 }, { month: "Jun", jobs: 267 }, { month: "Jul", jobs: 312 },
    ],
    revenue: [
      { month: "Feb", revenue: 284000 }, { month: "Mar", revenue: 376000 }, { month: "Apr", revenue: 441000 },
      { month: "May", revenue: 398000 }, { month: "Jun", revenue: 534000 }, { month: "Jul", revenue: 623000 },
    ],
    jobsTotal: 1330,
    revenueTotal: 2656000,
    jobsTrend: "17%",
    revenueTrend: "16%",
    jobsSubtitle: "Last 6 months",
    revenueSubtitle: "Last 6 months",
  },
};

// Helper: Format currency in KES
const formatKES = (amount) => {
  if (amount >= 1000000) return `KES ${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `KES ${(amount / 1000).toFixed(0)}K`;
  return `KES ${amount}`;
};

// Helper: Export CSV
const exportCSV = (data) => {
  const headers = ["Metric", "Value"];
  const rows = [
    ["Total Pros", data.totalPros],
    ["Active Customers", data.totalCustomers],
    ["Jobs Completed", data.completedJobs],
    ["Total Revenue", data.totalRevenue],
    ["Pending Approvals", data.pendingApprovals],
    ["Avg Rating", data.avgRating],
    ["Jobs Over Time", JSON.stringify(data.jobsOverTime)],
    ["Revenue Over Time", JSON.stringify(data.revenueOverTime)],
  ];
  
  const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `jirani-report-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Custom hook for admin overview data
const useAdminOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingMock, setUsingMock] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No admin token found');
      }

      const response = await fetch('http://localhost:5000/api/admin/overview', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('API request failed');

      const result = await response.json();
      if (result.success && result.data) {
        setData(result.data);
        setUsingMock(false);
        setError(null);
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      setData({
        totalPros: mockPros.length,
        totalCustomers: mockCustomers.filter(c => c.status === "active").length,
        completedJobs: mockJobs.filter(j => j.status === "completed").length,
        totalRevenue: mockPayments.filter(p => p.status === "completed").reduce((s, p) => s + p.amount, 0),
        pendingApprovals: mockPros.filter(p => p.verification === "pending").length,
        avgRating: (mockPros.reduce((s, p) => s + p.rating, 0) / mockPros.length).toFixed(1),
        jobsOverTime: mockChartData.jobsOverTime,
        revenueOverTime: mockChartData.revenueOverTime,
        recentActivity: mockActivityFeed,
      });
      setUsingMock(true);
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { data, loading, error, usingMock, refetch: fetchData };
};

// KPI Card Component - Fixed overflow
const KPICard = ({ label, value, icon: Icon, trend, trendValue, onClick }) => {
  const isPositive = trend === 'up';
  return (
    <div onClick={onClick} style={{
      minHeight: 96,
      padding: "16px 20px",
      borderRadius: 14,
      background: "#FFFFFF",
      border: "1px solid rgba(0,0,0,0.05)",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      cursor: "pointer",
      transition: "all 0.15s ease",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      gap: 8,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
      e.currentTarget.style.transform = "translateY(-1px)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
      e.currentTarget.style.transform = "translateY(0)";
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{
          width: 28, height: 28, background: "#F3F4F6",
          borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Icon size={14} color="#374151" />
        </div>
        <div style={{
          background: isPositive ? "#DCFCE7" : "#FEF2F2",
          padding: "2px 8px", borderRadius: 20, fontSize: 11,
          fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600,
          color: isPositive ? "#166534" : "#DC2626", whiteSpace: "nowrap",
        }}>
          {isPositive ? "↑" : "↓"} {trendValue}
        </div>
      </div>
      <div style={{
        fontSize: 20, fontWeight: 700,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: "#111827", lineHeight: 1.2,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {value}
      </div>
      <div style={{
        fontSize: 11, fontFamily: "'Plus Jakarta Sans', sans-serif",
        color: "#6B7280", overflow: "hidden",
        textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}>
        {label}
      </div>
    </div>
  );
};

// Bar Chart Component - Fixed layout
const BarChart = ({ data, valueKey, total, trend }) => {
  const [hoveredBar, setHoveredBar] = useState(null);
  const maxValue = Math.max(...data.map(d => d[valueKey]));
  
  return (
    <div style={{ overflow: "visible" }}>
      {/* Bars Container */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 120, marginTop: 16, position: "relative" }}>
        <div style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          height: 1,
          background: "rgba(0,0,0,0.04)",
          pointerEvents: "none",
        }} />
        
        {data.map((item, idx) => {
          const height = maxValue === 0 ? 0 : (item[valueKey] / maxValue) * 100;
          const isLast = idx === data.length - 1;
          
          return (
            <div 
              key={idx} 
              style={{ flex: 1, position: "relative", height: "100%", cursor: "pointer" }}
              onMouseEnter={() => setHoveredBar(idx)}
              onMouseLeave={() => setHoveredBar(null)}
            >
              <div style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: `${height}px`,
                background: isLast ? "#166534" : "#E5E7EB",
                borderRadius: "4px 4px 0 0",
                transition: "height 0.2s ease",
              }} />
              {hoveredBar === idx && (
                <div style={{
                  position: "absolute",
                  bottom: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  marginBottom: 4,
                  background: "#111827",
                  color: "#FFFFFF",
                  padding: "3px 7px",
                  borderRadius: 5,
                  fontSize: 11,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                  zIndex: 10,
                }}>
                  {valueKey === 'jobs' ? `${item[valueKey]} jobs` : formatKES(item[valueKey])}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Month Labels */}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        {data.map((item, idx) => (
          <div key={idx} style={{ 
            flex: 1, 
            textAlign: "center", 
            fontSize: 10,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            color: "#9CA3AF"
          }}>
            {item.month}
          </div>
        ))}
      </div>
      
      {/* Footer */}
      <div style={{
        marginTop: 12,
        paddingTop: 10,
        borderTop: "1px solid rgba(0,0,0,0.05)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}>
        <div style={{
          fontSize: 12,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          color: "#6B7280",
        }}>
          Total: <span style={{ fontWeight: 600, color: "#111827" }}>
            {valueKey === 'jobs' ? total : formatKES(total)}
          </span>
        </div>
        <div style={{
          fontSize: 12,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 600,
          color: "#166534",
        }}>
          ↑ {trend} this month
        </div>
      </div>
    </div>
  );
};

// Loading Skeleton Component
const LoadingSkeleton = () => (
  <div>
    <style>{`
      @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
    
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
      {[1,2,3,4,5,6].map(i => (
        <div key={i} style={{
          minHeight: 96,
          padding: "16px 20px",
          borderRadius: 14,
          background: "#FFFFFF",
          border: "1px solid rgba(0,0,0,0.05)",
          boxSizing: "border-box",
        }}>
          <div style={{
            width: 28,
            height: 28,
            background: "linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
            borderRadius: 8,
            marginBottom: 12,
          }} />
          <div style={{
            height: 20,
            width: "60%",
            background: "linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
            borderRadius: 4,
            marginBottom: 6,
          }} />
          <div style={{
            height: 11,
            width: "40%",
            background: "linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
            borderRadius: 4,
          }} />
        </div>
      ))}
    </div>
    
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
      {[1,2].map(i => (
        <div key={i} style={{
          padding: 24,
          borderRadius: 16,
          background: "#FFFFFF",
          border: "1px solid rgba(0,0,0,0.05)",
          boxSizing: "border-box",
        }}>
          <div style={{
            height: 20,
            width: "30%",
            background: "linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
            borderRadius: 4,
            marginBottom: 20,
          }} />
          <div style={{
            height: 120,
            background: "linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
            borderRadius: 8,
          }} />
        </div>
      ))}
    </div>
  </div>
);

// Modal Component for Add Category
const AddCategoryModal = ({ onClose, onSave }) => {
  const [categoryName, setCategoryName] = useState("");
  const [iconName, setIconName] = useState("");
  const [description, setDescription] = useState("");

  const handleSave = () => {
    console.log("Saving category:", { categoryName, iconName, description });
    onSave();
    onClose();
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: "#FFFFFF",
        borderRadius: 16,
        padding: 32,
        maxWidth: 480,
        width: "90%",
        boxSizing: "border-box",
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 20,
            fontWeight: 600,
            color: "#111827",
            margin: 0,
          }}>Add New Category</h2>
          <button onClick={onClose} style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 4,
          }}>
            <X size={20} color="#6B7280" />
          </button>
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            color: "#374151",
            display: "block",
            marginBottom: 6,
          }}>Category Name</label>
          <input
            type="text"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            placeholder="e.g., Plumbing"
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.1)",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              boxSizing: "border-box",
            }}
          />
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            color: "#374151",
            display: "block",
            marginBottom: 6,
          }}>Icon Name</label>
          <input
            type="text"
            value={iconName}
            onChange={(e) => setIconName(e.target.value)}
            placeholder="e.g., Droplets"
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.1)",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              boxSizing: "border-box",
            }}
          />
        </div>
        
        <div style={{ marginBottom: 24 }}>
          <label style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            color: "#374151",
            display: "block",
            marginBottom: 6,
          }}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of this category"
            rows={3}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.1)",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
        </div>
        
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            background: "#F3F4F6",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            color: "#374151",
            cursor: "pointer",
          }}>Cancel</button>
          <button onClick={handleSave} style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            background: "#166534",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            color: "#FFFFFF",
            cursor: "pointer",
          }}>Save Category</button>
        </div>
      </div>
    </div>
  );
};

export default function AdminOverview() {
  const navigate = useNavigate();
  const { data, loading, error, usingMock, refetch } = useAdminOverview();
  const [dateRange, setDateRange] = useState("last6months");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  if (loading) return <LoadingSkeleton />;
  if (error) {
    return (
      <div style={{
        background: "#FEF2F2",
        borderLeft: "3px solid #EF4444",
        padding: "12px 16px",
        marginBottom: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <span style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 13,
          color: "#991B1B",
        }}>Failed to load data</span>
        <button onClick={refetch} style={{
          padding: "4px 12px",
          borderRadius: 6,
          border: "none",
          background: "#DC2626",
          color: "#FFFFFF",
          fontSize: 12,
          cursor: "pointer",
        }}>Retry</button>
      </div>
    );
  }
  if (!data) return null;
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 500);
  };
  
  const chartData = CHART_DATA[dateRange];
  
  const KPI_DATA = [
    { label: "Total Pros", value: data.totalPros, icon: UserCheck, trend: "up", trendValue: "8%", route: "/admin/pros" },
    { label: "Active Customers", value: data.totalCustomers, icon: Users, trend: "up", trendValue: "12%", route: "/admin/customers" },
    { label: "Jobs Completed", value: data.completedJobs, icon: Briefcase, trend: "up", trendValue: "17%", route: "/admin/jobs" },
    { label: "Total Revenue", value: formatKES(data.totalRevenue), icon: TrendingUp, trend: "up", trendValue: "23%", route: "/admin/payments" },
    { label: "Pending Approvals", value: data.pendingApprovals, icon: Clock, trend: "down", trendValue: "5%", route: "/admin/pros?status=pending" },
    { label: "Avg Rating", value: `${data.avgRating} / 5`, icon: Star, trend: "up", trendValue: "0.2", route: "/admin/reviews" },
  ];
  
  const ALERTS = [
    { icon: Clock, color: "#EF4444", title: "Pending Verifications", subtitle: "Pros awaiting review", badge: "2", badgeBg: "#FEF2F2", badgeColor: "#EF4444", route: "/admin/notifications?type=verifications" },
    { icon: AlertTriangle, color: "#F59E0B", title: "Flagged Users", subtitle: "Suspicious activity", badge: "1", badgeBg: "#FFFBEB", badgeColor: "#F59E0B", route: "/admin/notifications?type=security" },
    { icon: CheckCircle, color: "#166534", title: "System Status", subtitle: "All systems operational", badge: "OK", badgeBg: "#ECFDF5", badgeColor: "#166534", route: null },
  ];
  
  return (
    <div>
      {/* Mock Data Warning */}
      {usingMock && (
        <div style={{
          background: "#FFFBEB",
          borderLeft: "3px solid #F59E0B",
          padding: "10px 16px",
          marginBottom: 20,
        }}>
          <span style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            color: "#92400E",
          }}>⚠️ Showing mock data — connect backend to see live data</span>
        </div>
      )}
      
      {/* Primary Action Row */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 20 }}>
        <button
          onClick={() => setIsModalOpen(true)}
          style={{
            background: "#166534",
            color: "#FFFFFF",
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >+ Add Category</button>
        <button
          onClick={() => navigate("/admin/pros?status=pending")}
          style={{
            background: "#F3F4F6",
            color: "#374151",
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >View Pending Pros</button>
        <button
          onClick={() => exportCSV(data)}
          style={{
            background: "#F3F4F6",
            color: "#374151",
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >Export Report</button>
      </div>
      
      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
        {KPI_DATA.map((kpi, idx) => (
          <KPICard
            key={idx}
            label={kpi.label}
            value={kpi.value}
            icon={kpi.icon}
            trend={kpi.trend}
            trendValue={kpi.trendValue}
            onClick={() => navigate(kpi.route)}
          />
        ))}
      </div>
      
      {/* Date Range Filter */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "20px 0" }}>
        <div style={{
          background: "#F3F4F6",
          borderRadius: 10,
          padding: 3,
          display: "flex",
          gap: 2,
        }}>
          {["thisweek", "thismonth", "last6months"].map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                border: "none",
                background: dateRange === range ? "#FFFFFF" : "transparent",
                boxShadow: dateRange === range ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 13,
                fontWeight: dateRange === range ? 600 : 400,
                color: dateRange === range ? "#111827" : "#6B7280",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {range === "thisweek" ? "This week" : range === "thismonth" ? "This month" : "Last 6 months"}
            </button>
          ))}
        </div>
        
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          style={{
            background: "#F3F4F6",
            borderRadius: 8,
            padding: 8,
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <RefreshCw size={16} color="#6B7280" style={{
            animation: isRefreshing ? "spin 0.5s linear infinite" : "none",
          }} />
        </button>
      </div>
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      {/* Charts Section */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Jobs Chart */}
        <div style={{
          padding: 24,
          borderRadius: 16,
          background: "#FFFFFF",
          border: "1px solid rgba(0,0,0,0.05)",
          boxSizing: "border-box",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h3 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                margin: 0,
                marginBottom: 4,
              }}>Jobs Over Time</h3>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12,
                color: "#6B7280",
              }}>{chartData.jobsSubtitle}</div>
            </div>
            <BarChart3 size={16} color="#9CA3AF" />
          </div>
          <BarChart 
            data={chartData.jobs} 
            valueKey="jobs" 
            total={chartData.jobsTotal}
            trend={chartData.jobsTrend}
          />
        </div>
        
        {/* Revenue Chart */}
        <div style={{
          padding: 24,
          borderRadius: 16,
          background: "#FFFFFF",
          border: "1px solid rgba(0,0,0,0.05)",
          boxSizing: "border-box",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h3 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 16,
                fontWeight: 600,
                color: "#111827",
                margin: 0,
                marginBottom: 4,
              }}>Revenue Over Time</h3>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12,
                color: "#6B7280",
              }}>{chartData.revenueSubtitle}</div>
            </div>
            <DollarSign size={16} color="#9CA3AF" />
          </div>
          <BarChart 
            data={chartData.revenue} 
            valueKey="revenue" 
            total={chartData.revenueTotal}
            trend={chartData.revenueTrend}
          />
        </div>
      </div>
      
      {/* Alerts + Activity Feed */}
      <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 20 }}>
        {/* Alerts Panel */}
        <div style={{
          padding: 24,
          borderRadius: 16,
          background: "#FFFFFF",
          border: "1px solid rgba(0,0,0,0.05)",
          boxSizing: "border-box",
        }}>
          <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 16,
            fontWeight: 600,
            color: "#111827",
            margin: 0,
            marginBottom: 16,
          }}>Alerts & notifications</h3>
          
          {ALERTS.map((alert, idx) => (
            <div
              key={idx}
              onClick={() => alert.route && navigate(alert.route)}
              style={{
                height: 56,
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "0 12px",
                borderBottom: idx < ALERTS.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none",
                cursor: alert.route ? "pointer" : "default",
                transition: "background 0.15s ease",
              }}
              onMouseEnter={(e) => {
                if (alert.route) e.currentTarget.style.background = "#F9FAFB";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <alert.icon size={16} color={alert.color} />
              <div style={{ flex: 1 }}>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#111827",
                  marginBottom: 2,
                }}>{alert.title}</div>
                <div style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 12,
                  color: "#6B7280",
                }}>{alert.subtitle}</div>
              </div>
              <div style={{
                background: alert.badgeBg,
                padding: "2px 8px",
                borderRadius: 20,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12,
                fontWeight: 600,
                color: alert.badgeColor,
              }}>{alert.badge}</div>
            </div>
          ))}
        </div>
        
        {/* Activity Feed */}
        <div style={{
          padding: 24,
          borderRadius: 16,
          background: "#FFFFFF",
          border: "1px solid rgba(0,0,0,0.05)",
          boxSizing: "border-box",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 16,
              fontWeight: 600,
              color: "#111827",
              margin: 0,
            }}>Recent activity</h3>
            <button
              onClick={() => navigate("/admin/activity")}
              style={{
                background: "none",
                border: "none",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 13,
                color: "#166534",
                cursor: "pointer",
              }}
            >View all →</button>
          </div>
          
          {data.recentActivity && data.recentActivity.length > 0 ? (
            data.recentActivity.slice(0, 6).map((item, idx) => {
              const iconMap = {
                "user-check": UserCheck,
                "check-circle": CheckCircle,
                "credit-card": CreditCard,
                "alert-triangle": AlertTriangle,
                "user-plus": UserPlus,
                "star": Star,
              };
              const IconComp = iconMap[item.icon] || UserCheck;
              
              return (
                <div
                  key={item.id || idx}
                  style={{
                    height: 56,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "0 12px",
                    borderBottom: idx < 5 ? "1px solid rgba(0,0,0,0.04)" : "none",
                    transition: "background 0.15s ease",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <div style={{
                    width: 28,
                    height: 28,
                    background: "#F3F4F6",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    <IconComp size={14} color="#374151" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 13,
                      color: "#374151",
                    }}>{item.message}</div>
                  </div>
                  <div style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12,
                    color: "#9CA3AF",
                    whiteSpace: "nowrap",
                  }}>{item.time}</div>
                </div>
              );
            })
          ) : (
            <div style={{
              textAlign: "center",
              padding: "40px 20px",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 13,
              color: "#9CA3AF",
            }}>No recent activity</div>
          )}
        </div>
      </div>
      
      {/* Add Category Modal */}
      {isModalOpen && (
        <AddCategoryModal
          onClose={() => setIsModalOpen(false)}
          onSave={() => {
            console.log("Category saved");
          }}
        />
      )}
    </div>
  );
}